import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { Cookie, Settings2 } from 'lucide-react';
import { useConsent } from '@/contexts/ConsentContext';
import { Button } from '@/components/ui/button';

const FONT_H = { fontFamily: "'Outfit', sans-serif" };
const FONT_B = { fontFamily: "'Manrope', sans-serif" };

/**
 * Bottom-anchored cookie-consent banner.
 * Only rendered when the user hasn't made a decision against the current policy version.
 */
export function CookieConsentBanner() {
  const { hasDecided, acceptAll, rejectAll, openPreferences } = useConsent();
  const { pathname } = useLocation();

  // Passive clubhouse/TV screens have nobody to click it, and the banner
  // covers the bottom of the board. No first-party cookies are set on them.
  const isTvScreen = pathname.startsWith('/tv/');

  return (
    <AnimatePresence>
      {!hasDecided && !isTvScreen && (
        <motion.div
          initial={{ y: 140, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 140, opacity: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="fixed bottom-0 inset-x-0 z-[9999] p-3 sm:p-4"
          data-testid="cookie-consent-banner"
          role="dialog"
          aria-labelledby="cookie-consent-title"
          aria-describedby="cookie-consent-desc"
        >
          <div
            className="max-w-5xl mx-auto bg-white rounded-2xl shadow-[0_12px_48px_rgba(0,0,0,0.18)] border border-gray-100 overflow-hidden"
            style={{ ...FONT_B, colorScheme: 'light' }}
          >
            <div className="flex flex-col md:flex-row md:items-center gap-4 p-4 sm:p-5">
              <div className="flex items-start gap-3 flex-1">
                <div className="w-9 h-9 rounded-xl bg-pink-50 flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-5 h-5 text-pink-500" />
                </div>
                <div className="min-w-0">
                  <h2 id="cookie-consent-title" className="text-sm font-bold text-gray-900" style={FONT_H}>
                    We value your privacy
                  </h2>
                  <p id="cookie-consent-desc" className="text-xs text-gray-600 mt-1 leading-relaxed">
                    We only use cookies that are strictly necessary to keep you signed in and process your payments.
                    Optional categories (analytics, marketing) are off by default - you choose.{' '}
                    <a href="/cookie-policy" className="underline underline-offset-2 hover:text-pink-600">
                      Cookie Policy
                    </a>
                    {' · '}
                    <a href="/privacy" className="underline underline-offset-2 hover:text-pink-600">
                      Privacy Policy
                    </a>
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 md:flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openPreferences}
                  className="text-xs h-9 px-3 rounded-full !bg-white !border-gray-200 hover:!bg-gray-50"
                  data-testid="cookie-customise-btn"
                >
                  <Settings2 className="w-3.5 h-3.5 mr-1.5" style={{ color: '#E91E8C' }} />
                  <span
                    className="bg-clip-text"
                    style={{
                      backgroundImage: 'linear-gradient(135deg, #E91E8C 0%, #6C3CE0 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      color: 'transparent',
                    }}
                  >
                    Customise
                  </span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={rejectAll}
                  className="text-xs h-9 px-4 rounded-full !bg-white !border-gray-200 hover:!bg-gray-50"
                  data-testid="cookie-reject-all-btn"
                >
                  <span
                    className="bg-clip-text"
                    style={{
                      backgroundImage: 'linear-gradient(135deg, #E91E8C 0%, #6C3CE0 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      color: 'transparent',
                    }}
                  >
                    Reject all
                  </span>
                </Button>
                <Button
                  size="sm"
                  onClick={acceptAll}
                  className="text-xs h-9 px-4 rounded-full text-white"
                  style={{ background: 'linear-gradient(135deg, #E91E8C 0%, #6C3CE0 100%)' }}
                  data-testid="cookie-accept-all-btn"
                >
                  Accept all
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
